// =====================================================
// 时间格式化：信息流列表 / 详情页统一使用的相对时间
//
// 后端返回的 createTime 形如 "2026-07-31 12:00:00"，
// Safari / 部分微信内核不支持 "-" 分隔的日期字符串，必须替换成 "/" 再 new Date。
// =====================================================

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function pad (n) {
  return String(n).padStart(2, '0')
}

// 字符串 / 时间戳 → Date，解析失败返回 null
export function parseTime (value) {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  const date = new Date(String(value).replace(/-/g, '/'))
  return isNaN(date.getTime()) ? null : date
}

/**
 * 信息流卡片用的相对时间
 * 刚刚 / x分钟前 / x小时前 / 昨天 HH:mm / MM-DD / YYYY-MM-DD
 */
export function formatFeedTime (value) {
  const date = parseTime(value)
  if (!date) return ''

  const now = new Date()
  const diff = now.getTime() - date.getTime()

  // 服务端时间略微超前时按「刚刚」处理，避免出现负数
  if (diff < MINUTE) return '刚刚'
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}分钟前`

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart)
  yesterdayStart.setDate(yesterdayStart.getDate() - 1)

  if (date >= todayStart) return `${Math.floor(diff / HOUR)}小时前`
  if (date >= yesterdayStart) return `昨天 ${pad(date.getHours())}:${pad(date.getMinutes())}`
  if (date.getFullYear() === now.getFullYear()) {
    return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// =====================================================
// 微信式聊天时间
//
// 私聊里的时间要读起来像人话，"2026-08-02 22:50:35" 这种机器格式不合适。
// 对齐微信的显示规则：
//   今天        凌晨2:01 / 上午10:11 / 中午12:12 / 下午4:33 / 晚上9:07
//   昨天        昨天 上午10:13
//   2~6 天前    星期三 下午3:20
//   今年更早    7月1日 上午9:00
//   跨年        2025年7月1日 上午9:00
//
// 时段划分：0~5 凌晨 / 6~11 上午 / 12 中午 / 13~17 下午 / 18~23 晚上
// 时钟部分用 12 小时制、小时不补零、分钟补零，不带秒（微信同样不显示秒）。
// =====================================================

const WEEK_NAMES = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']

/**
 * 时段前缀 + 12 小时制的「时:分」，如 下午4:33
 */
function formatClock (date) {
  const h = date.getHours()

  let period
  if (h < 6) period = '凌晨'
  else if (h < 12) period = '上午'
  else if (h < 13) period = '中午'
  else if (h < 18) period = '下午'
  else period = '晚上'

  // 12 小时制：13~23 点减 12；12 点保持 12（中午12:12）；
  // 0 点保持 0，中文习惯读作「凌晨0:30」而不是「凌晨12:30」
  const hour12 = h > 12 ? h - 12 : h

  return `${period}${hour12}:${pad(date.getMinutes())}`
}

/**
 * 相隔多少个自然日（只比日期，不看时分秒）。
 * 用自然日而不是 86400000 整除：23:50 和次日 00:10 只差 20 分钟，
 * 但对用户来说已经是"昨天"了。
 */
function calendarDayDiff (from, to) {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime()
  return Math.round((end - start) / DAY)
}

/**
 * 私聊消息时间（微信风格）
 * @param {String|Number|Date} value 服务端时间，形如 "2026-08-02 22:50:35"
 * @param {Date} [now] 参照的当前时间，便于同一批消息复用同一个基准
 */
export function formatChatTime (value, now) {
  const date = parseTime(value)
  if (!date) return ''

  const current = now instanceof Date && !isNaN(now.getTime()) ? now : new Date()
  const clock = formatClock(date)
  const diff = calendarDayDiff(date, current)

  // diff < 0 说明服务端时钟略微超前，按今天处理，别显示成未来日期
  if (diff <= 0) return clock
  if (diff === 1) return `昨天 ${clock}`
  // 7 天以内用星期几；正好第 7 天的星期名和今天重复，会读成"今天"，所以不含 7
  if (diff < 7) return `${WEEK_NAMES[date.getDay()]} ${clock}`
  if (date.getFullYear() === current.getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日 ${clock}`
  }
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${clock}`
}

export default formatFeedTime
